// TODO - Type 'any' needs to be fixed.
/* eslint-disable no-mixed-spaces-and-tabs */
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { GridOverlay, useGridApiRef } from "@mui/x-data-grid-pro";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import DataGrid from "../../../../../design/wilya-datagrid";
import useViewport from "../../././../../../utils/use-viewport";

interface RowData {
	shiftTime: string;
	shiftDateTime: string;
	workersRequired: number;
	hoursScheduled: string;
	id: number;
	jobRole: any; // Adjust the type accordingly
	date: string;
	time: { start: string; end: string };
	createdByName: string;
	totalPublishedTime: number;
	shiftDurationInMinutes: number;
	restrictFlexHours: boolean;
	skills?: string[];
	isUrgent?: boolean;
}

type ReviewPublishingShiftsTableProps = {
	rows: RowData[];
	columns: any[];
	handleShiftSelection?: (params: any) => void;
	checkAllowedToPublishStatus: string;
	checkAllowedToPublishData: any;
	selectedShifts?: any[];
	timeLeftByJobs: any;
	workerCountByShift: any;
	workerCountByShiftStatus: string;
};

interface RowWithTimeRemaining extends RowData {
	timeRemaining?: number;
	allowedToPublish?: boolean;
	timeExceeded: boolean;
}

function NoShiftsAvailableForPublish() {
	const { t } = useTranslation();
	return (
		<GridOverlay>
			<Box display='flex' flexDirection='column' alignItems='center'>
				<CalendarTodayIcon fontSize='large' />
				<Typography>{t("scheduler_no-draft-shifts-found")}</Typography>
			</Box>
		</GridOverlay>
	);
}

const ReviewPublishingShiftsTable = ({
	rows,
	columns,
	handleShiftSelection,
	checkAllowedToPublishStatus,
	checkAllowedToPublishData,
	selectedShifts,
	timeLeftByJobs,
	workerCountByShift,
	workerCountByShiftStatus,
}: ReviewPublishingShiftsTableProps) => {
	const { t } = useTranslation();
	const { height } = useViewport();
	const apiRef = useGridApiRef();
	const [loading, setLoading] = useState(true);

	const restrictFlexShifts = useSelector(
		(state: any) => state.user.userData.restrictFlexHours
	);

	const rowsWithTimeRemaining: RowWithTimeRemaining[] = rows.map((row: any) => {
		if (selectedShifts?.includes(row?.id)) {
			return {
				...row,
				timeRemaining:
					!!row && checkAllowedToPublishData[row.id]?.timeRemaining,
				allowedToPublish:
					!!row && checkAllowedToPublishData[row.id]?.allowedToPublish,
				timeExceeded: false,
			};
		}
		return {
			...row,
			timeRemaining: checkAllowedToPublishData[row.id]?.timeRemaining,
			allowedToPublish: checkAllowedToPublishData[row.id]?.allowedToPublish,
			timeExceeded:
				row &&
				timeLeftByJobs[row.jobRole?.id] * 60 <
					row.shiftDurationInMinutes * row.workersRequired,
		};
	});

	const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
		number[]
	>([]);
	const [expandedRowIds, setExpandedRowIds] = useState<number[]>([]);

	const getDetailPanelContent = useCallback(({ row }: any) => {
		// The Shift Purpose's question is unanswered — publishing rejects the
		// whole batch for it, so it is the most blocking reason to show.
		if (row.isMissingPurposeDetail) {
			return (
				<Alert severity='error'>
					{t("scheduler_purpose-detail-required", {
						label: row.purposeDetailLabel,
					})}
				</Alert>
			);
		}
		if (!row.allowedToPublish && row.timeExceeded) {
			return (
				<Alert severity='error'>
					{t("scheduler_exceed-monthly-flex-limit", {
						jobRole: row.jobRole.name,
						hours: row.timeRemaining,
					})}
				</Alert>
			);
		}
		return null;
	}, [t]);

	const getDetailPanelWorkerCount = useCallback(({ row }: any) => {
		if (row.isMissingPurposeDetail) {
			return (
				<Alert severity='error'>
					{t("scheduler_purpose-detail-required", {
						label: row.purposeDetailLabel,
					})}
				</Alert>
			);
		}
		return (
			<Alert severity='error'>
				{t("scheduler_no-eligible-workers-selected-shifts")}
			</Alert>
		);
	}, [t]);

	const getDetailPanelHeight = useCallback(() => 50, []);

	useEffect(() => {
		if (!restrictFlexShifts) return;
		const expandedIds: number[] = rows
			.filter((row) => !checkAllowedToPublishData[row.id]?.allowedToPublish)
			.map((row) => row.id);
		setDetailPanelExpandedRowIds(expandedIds);
	}, [rows, checkAllowedToPublishData, selectedShifts, restrictFlexShifts]);

	const missingPurposeDetailRowIds = useMemo(
		() =>
			rows
				.filter((row: any) => row.isMissingPurposeDetail)
				.map((row: any) => row.id),
		[rows]
	);

	useEffect(() => {
		if (missingPurposeDetailRowIds.length === 0) return;
		const remaining = (selectedShifts ?? []).filter(
			(shiftId) => !missingPurposeDetailRowIds.includes(shiftId)
		);
		if (remaining.length !== (selectedShifts ?? []).length) {
			handleShiftSelection?.(remaining);
		}
	}, [missingPurposeDetailRowIds, selectedShifts, handleShiftSelection]);

	useEffect(() => {
		if (workerCountByShift) {
			// get ids with count 0
			const ids = Object.keys(workerCountByShift).filter(
				(id) => workerCountByShift[id] === 0
			);
			const expandedIds = ids.map((id) => parseInt(id));
			setExpandedRowIds(expandedIds);
		}
	}, [workerCountByShift]);

	useEffect(() => {
		if (restrictFlexShifts) {
			if (
				checkAllowedToPublishStatus === "fulfilled" &&
				rows.length > 0 &&
				JSON.stringify(checkAllowedToPublishData) !== "{}"
			) {
				setLoading(false);
			} else if (rows.length === 0) {
				setLoading(false);
			} else if (checkAllowedToPublishStatus === "error") {
				// The check rejects the whole batch when a Shift Purpose question
				// is unanswered, so no per-shift data arrives. Show the Shifts
				// with the error rather than spinning forever.
				setLoading(false);
			}
		} else {
			setLoading(false);
		}
	}, [
		restrictFlexShifts,
		checkAllowedToPublishStatus,
		checkAllowedToPublishData,
		selectedShifts,
		rows,
	]);

	useEffect(() => {
		if ((selectedShifts?.length ?? 0) > 0 && workerCountByShift) {
			// Check if any selected shifts have 0 workers
			const shiftsToDeselect: number[] =
				selectedShifts?.filter(
					(shiftId) => workerCountByShift[shiftId] === 0
				) ?? [];

			if (shiftsToDeselect.length > 0) {
				const newSelection = selectedShifts?.filter(
					(shiftId) => !shiftsToDeselect.includes(shiftId)
				);
				if (handleShiftSelection) {
					handleShiftSelection(newSelection);
				}
			}
		}
	}, [workerCountByShift, selectedShifts, handleShiftSelection]);

	const finalRows = !restrictFlexShifts
		? rows
		: Object.keys(checkAllowedToPublishData).length === 0
		? []
		: rowsWithTimeRemaining;

	return (
  <Box width="100%" height={height - 383}>
			{finalRows.length === 1 ? (
				<DataGrid
					apiRef={apiRef}
					rows={finalRows}
					columns={columns}
					checkboxSelection
					isRowSelectable={(params: any) =>
						params.row.isMissingPurposeDetail
							? false
							: params.row.restrictFlexHours &&
							  expandedRowIds.length === 0
							? !params.row.timeExceeded
							: expandedRowIds.includes(params.row.id)
							? false
							: true
					}
					hideFooter={true}
					selectionModel={selectedShifts}
					onSelectionModelChange={handleShiftSelection}
					disableRowSelectionOnClick
					disableColumnSelector
					disableDensitySelector
					disableColumnPinning
					disableColumnReorder
					disableColumnResize
					loading={loading || workerCountByShiftStatus === "pending"}
					components={{
						NoRowsOverlay: NoShiftsAvailableForPublish,
					}}
					// getRowHeight={() => "auto"}
					detailPanelExpandedRowIds={[
						...detailPanelExpandedRowIds,
						...expandedRowIds,
						...missingPurposeDetailRowIds,
					]}
					getDetailPanelContent={
						restrictFlexShifts && expandedRowIds.length === 0
							? getDetailPanelContent
							: getDetailPanelWorkerCount
					}
					getDetailPanelHeight={getDetailPanelHeight}
					sx={{
						border: "none !important",
						"& .MuiDataGrid-columnSeparator": {
							display: "none",
						},
						"& .MuiDataGrid-row": {
							border: "none !important",
						},
						"& .MuiDataGrid-columnHeaderCheckbox": {
							visibility: "hidden",
						},
					}}
				/>
			) : (
				<DataGrid
					apiRef={apiRef}
					rows={finalRows}
					columns={columns}
					checkboxSelection
					isRowSelectable={(params: any) =>
						params.row.isMissingPurposeDetail
							? false
							: params.row.restrictFlexHours &&
							  expandedRowIds.length === 0
							? !params.row.timeExceeded
							: expandedRowIds.includes(params.row.id)
							? false
							: true
					}
					hideFooter={true}
					onSelectionModelChange={handleShiftSelection}
					disableRowSelectionOnClick
					disableColumnSelector
					disableDensitySelector
					disableColumnPinning
					disableColumnReorder
					disableColumnResize
					loading={loading || workerCountByShiftStatus === "pending"}
					components={{
						NoRowsOverlay: NoShiftsAvailableForPublish,
					}}
					// getRowHeight={() => "auto"}
					detailPanelExpandedRowIds={[
						...detailPanelExpandedRowIds,
						...expandedRowIds,
						...missingPurposeDetailRowIds,
					]}
					getDetailPanelContent={
						restrictFlexShifts && expandedRowIds.length === 0
							? getDetailPanelContent
							: getDetailPanelWorkerCount
					}
					getDetailPanelHeight={getDetailPanelHeight}
					sx={{
						border: "none !important",
						"& .MuiDataGrid-columnSeparator": {
							display: "none",
						},
						"& .MuiDataGrid-row": {
							border: "none !important",
						},
						"& .MuiDataGrid-columnHeaderCheckbox": {
							visibility: "hidden",
						},
					}}
				/>
			)}
		</Box>
	);
};

export default ReviewPublishingShiftsTable;
